import { api, formatDateRange, formatApiError } from "@/lib/api";
import { displayFor, uiToBackendStatus, effectiveStatus } from "@/lib/bills-util";
import { toast } from "sonner";
import PayPeriodForecast from "@/components/PayPeriodForecast";

// Shows the current pay period (containing today) with:
//   • Cash Flow Forecast card (Steps 1/2/3 + end-of-period balance)
//   • Reconcile / Bill list card — visually separated below with its own
//     amber background rectangle so bills read as a distinct action zone,
//     not a foot-note to the forecast.
export default function CurrentPayPeriodSection({
  payPeriods, monthWindow, viewMode, onViewModeChange, fundingSources, bills, pending, incomes, user,
  onBillUpdated, onReschedule, onIncomeChanged, onOpenReconcile, onSourceUpdated, onPendingChanged, onRefresh,
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // viewMode + changeViewMode now come from parent so both the current-period
  // card AND the "Your next…" strip below share the same state.
  const changeViewMode = onViewModeChange;

  // Find the pay period whose window contains today. Falls back to the first upcoming.
  const realCurrentPP = (payPeriods || []).find((p) => {
    const s = new Date(p.period_start + "T00:00:00");
    const e = new Date(p.period_end + "T00:00:00");
    return s <= today && today <= e;
  }) || (payPeriods || []).find((p) => new Date(p.period_end + "T00:00:00") >= today) || null;

  // Synthesize a "virtual pay period" for month view — same shape the
  // downstream component expects, but with month boundaries as the window.
  // Income projection comes from the backend `/api/forecast/month` endpoint
  // (mirrors pay-period logic exactly) so recurring paydays don't disappear
  // when the base income row is marked "received" — a bug the frontend-only
  // projector had on real user accounts. See forecast.project_incomes_in_window.
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const monthCurrentPP = {
    period_start: (monthWindow && monthWindow.period_start) || monthStart.toISOString().slice(0, 10),
    period_end: (monthWindow && monthWindow.period_end) || monthEnd.toISOString().slice(0, 10),
    expected_income_breakdown: (monthWindow && monthWindow.expected_income_breakdown) || [],
    expected_income_amount: (monthWindow && monthWindow.expected_income_amount) || 0,
  };
  // "Next 30 days" — rolling window starting today. Uses the same window
  // shape as the pay-period + month cases so the downstream forecast card
  // Just Works. Income projection falls back to the frontend projector
  // (projectIncomeOccurrences) which handles recurring paychecks correctly
  // for an arbitrary window when no backend expected_income_breakdown is
  // supplied.
  const next30End = new Date(today);
  next30End.setDate(next30End.getDate() + 30);
  const next30CurrentPP = {
    period_start: today.toISOString().slice(0, 10),
    period_end: next30End.toISOString().slice(0, 10),
    // Deliberately omit `expected_income_breakdown` so PayPeriodForecast
    // falls back to the client-side income projector for this arbitrary
    // rolling window (backend only provides breakdowns for real pay periods
    // and calendar months). Passing an empty array here would short-circuit
    // the projector and cause Step 3 to show no incoming amounts.
    expected_income_amount: 0,
  };
  let currentPP;
  if (viewMode === "month") currentPP = monthCurrentPP;
  else if (viewMode === "next-30") currentPP = next30CurrentPP;
  else currentPP = realCurrentPP;
  const viewLabel = viewMode === "month"
    ? `This Month · ${monthStart.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`
    : viewMode === "next-30"
      ? `Next 30 days · ${formatDateRange(today.toISOString().slice(0, 10), next30End.toISOString().slice(0, 10))}`
      : realCurrentPP
        ? `Current Pay Period · ${formatDateRange(realCurrentPP.period_start, realCurrentPP.period_end)}`
        : "";

  if (!currentPP) {
    return (
      <div className="card-soft p-6" data-testid="current-pay-period-empty">
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Current pay period</div>
        <p className="text-sm text-[#59605C] mt-2">Add a recurring income to see your current pay period breakdown.</p>
      </div>
    );
  }

  const periodStart = new Date(currentPP.period_start + "T00:00:00");
  const periodEnd = new Date(currentPP.period_end + "T00:00:00");

  // Bills that belong to THIS pay period — now including past-due bills that
  // haven't been rescheduled forward. Rules:
  //   • Any bill due within [periodStart, periodEnd] is shown here.
  //   • Any bill due BEFORE periodStart that is still effectively unpaid
  //     (i.e., not cleared, not paid+notCleared-already-past) also stays here,
  //     because those are dollars that still have to come out of current cash.
  //     This replaces the old "Bills carried over from prior periods"
  //     forecast section — everything lives in one place now.
  // Within each of the three visual bands (Actionable / Waiting / Paid),
  // rows are sorted by due_date ascending (earliest at the top).
  const periodBills = bills
    .filter((b) => {
      const d = new Date(b.due_date + "T00:00:00");
      if (d > periodEnd) return false;                     // future bill → out
      if (d >= periodStart) return true;                   // in current period
      // Past-due carry-in: keep it if it's not fully settled yet.
      if (b.status === "cleared") return false;
      if (b.status === "paid" && !displayFor(b, today).notCleared) return false;
      return true;
    })
    .map((b) => {
      const disp = displayFor(b, today);
      const eff = effectiveStatus(b, today);
      // Three visual bands: "actionable" (unpaid or overdue), "waiting" (pending
      // or paid-but-not-cleared), "paid" (paid AND cleared). notCleared bills
      // stay in the "waiting" band because they still need to hit the account.
      // effectiveStatus keeps future-dated autopay bills in "actionable" until
      // their charge date passes, even if the user pre-marked them as pending.
      let band = "paid";
      if (eff === "unpaid") band = "actionable";
      else if (eff === "pending" || (eff === "paid" && disp.notCleared)) band = "waiting";
      return { b, band, disp };
    })
    .sort((a, b) => {
      const bandOrder = { actionable: 0, waiting: 1, paid: 2 };
      if (bandOrder[a.band] !== bandOrder[b.band]) return bandOrder[a.band] - bandOrder[b.band];
      return a.b.due_date.localeCompare(b.b.due_date);
    });

  const setBillStatus = async (bill, uiStatus) => {
    const backendStatus = uiToBackendStatus(uiStatus);
    try {
      const upd = { status: backendStatus };
      if (["paid", "cleared", "pending"].includes(backendStatus)) upd.paid_amount = bill.estimated_due_amount;
      else if (backendStatus === "unpaid") upd.paid_amount = 0;
      const { data } = await api.patch(`/bills/${bill.id}`, upd);
      onBillUpdated(data);
      toast.success(`${bill.name} → ${uiStatus}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      {/* Card A — Cash Flow Forecast (Steps 1 → 2 → 3 → end-of-period balance).
          Rendered directly as a single container (no outer wrapper) so on
          mobile it reclaims the double-padding + duplicate-header space
          that the old nested-card layout wasted. The status badge (Safe /
          Attention) is passed inline into the forecast header. */}
      <PayPeriodForecast
        currentPP={currentPP}
        bills={bills}
        incomes={incomes}
        pending={pending}
        fundingSources={fundingSources}
        onBillUpdated={onBillUpdated}
        onSourceUpdated={onSourceUpdated}
        onIncomeChanged={onIncomeChanged}
        onPendingChanged={onPendingChanged}
        onRefresh={onRefresh}
        viewMode={viewMode}
        onViewModeChange={changeViewMode}
        viewLabel={viewLabel}
        statusLabel={viewMode === "pay-period" && realCurrentPP ? realCurrentPP.status_label : null}
        balanceEditStreak={user?.balance_edit_streak || 0}
        periodBills={viewMode === "pay-period" ? periodBills : null}
        user={user}
        onReschedule={onReschedule}
        setBillStatus={setBillStatus}
      />
    </>
  );
}
